import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import useFetch from 'use-http';
import {
  InfoWrapper,
  RHFCheckbox,
  RHFDatePicker,
  RHFMultiSelect,
  RHFNumberTextField,
  RHFSelect,
  RHFTextField,
} from 'components';
import { INSPECTIONS_ROOT } from 'constants/api';
import { PossibileAccessoSottoPonte } from 'constants/inspections';
import {
  AffidabilitaComplessivaRischioFraneOptions,
  AffidabilitaComplessivaRischioIdraulicoOptions,
  PossibileAccessoSottoPonteOptions,
  ProspettiIspezionatiOptions,
  RischioFranaOptions,
  RischioIdraulicoOptions,
  VerificaScalzamentoFondazioniOptions,
} from 'constants/selectOptions';

import { useInspectionPath } from 'hooks';
import { useInspectionSelector } from 'stores';
import {
  CheckboxContainer,
  GridWrap,
  TitleSection,
} from './Accordion2InspectionInformation.styles';
import { INFO } from '../../../../config/info';

const Accordion2InspectionInformationL1 = () => {
  // hooks
  const { get } = useFetch();
  const { setValue, watch } = useFormContext();
  const { inspectionDisabled } = useInspectionSelector();
  const { isViewingInspection } = useInspectionPath();
  const params = useParams();
  const inspectionId = params.inspectionId;

  const watchDataIspezione = watch('dataIspezioneRischioStrutturale');
  const watchPossibileAccessoSottoPonte = watch('accessoSottoPonte');
  const watchPossibileAccessoSottoPonteParziale =
    watchPossibileAccessoSottoPonte === PossibileAccessoSottoPonte.Parziale;

  const watchDataIspezioneRischioFrane = watch('dataIspezioneRischioFrane');
  const watchDataIspezioneRischioIdraulico = watch(
    'dataIspezioneRischioIdraulico'
  );

  //Functions
  useEffect(() => {
    !watchDataIspezione && setValue('dataIspezioneRischioStrutturale', null);
  }, [watchDataIspezione]);

  useEffect(() => {
    !watchDataIspezioneRischioFrane &&
      setValue('dataIspezioneRischioFrane', null);
  }, [watchDataIspezioneRischioFrane]);

  useEffect(() => {
    !watchDataIspezioneRischioIdraulico &&
      setValue('dataIspezioneRischioIdraulico', null);
  }, [watchDataIspezioneRischioIdraulico]);

  useEffect(() => {
    !watchPossibileAccessoSottoPonteParziale &&
      setValue('accessoSottoPonteParziale', '');
  }, [watchPossibileAccessoSottoPonte]);

  // -------------------------------- PROSSIME ISPEZIONI --------------------------------
  const [nextIspectionDates, setNexInspectionDates] = useState();
  const dataProssimaIspezioneOrdinariaCalculated =
    nextIspectionDates?.systemNextOrdinaryInspectionDate;

  // Data prossima ispezione ordinaria
  const watchProssimaIspezioneOrdinaria = watch(
    'dataProssimaIspezioneOrdinaria'
  );

  useEffect(() => {
    if (!inspectionId) return;
    if (
      dataProssimaIspezioneOrdinariaCalculated &&
      watchProssimaIspezioneOrdinaria
    ) {
      const nextOrdinaryInspectionDate = new Date(
        dataProssimaIspezioneOrdinariaCalculated
      )
        .toISOString()
        .split('T')[0];
      const watchOrdinaryInspectionDate = new Date(
        watchProssimaIspezioneOrdinaria
      )
        .toISOString()
        .split('T')[0];

      if (nextOrdinaryInspectionDate === watchOrdinaryInspectionDate) {
        setValue('tipologiaOrdinariaLineeGuida', true);
        setValue('tipologiaOrdinariaIspettore', false);
      } else {
        setValue('tipologiaOrdinariaLineeGuida', false);
        setValue('tipologiaOrdinariaIspettore', true);
      }
    }

    if (!watchProssimaIspezioneOrdinaria) {
      setValue('tipologiaOrdinariaLineeGuida', false);
      setValue('tipologiaOrdinariaIspettore', false);
    }

    if (
      watchProssimaIspezioneOrdinaria &&
      !dataProssimaIspezioneOrdinariaCalculated
    ) {
      setValue('tipologiaOrdinariaLineeGuida', false);
      setValue('tipologiaOrdinariaIspettore', true);
    }
  }, [
    inspectionId,
    dataProssimaIspezioneOrdinariaCalculated,
    watchProssimaIspezioneOrdinaria,
  ]);

  // Data prossima ispezione straordinaria
  const dataProssimaIspezioneStraordinariaCalculated =
    nextIspectionDates?.systemNextExtraordinaryInspectionDate;
  const watchProssimaIspezioneStraordinaria = watch(
    'dataProssimaIspezioneStraordinaria'
  );

  useEffect(() => {
    if (!inspectionId) return;
    if (
      dataProssimaIspezioneStraordinariaCalculated &&
      watchProssimaIspezioneStraordinaria
    ) {
      const nextStraordinaryInspectionDate = new Date(
        dataProssimaIspezioneStraordinariaCalculated
      )
        .toISOString()
        .split('T')[0];
      const watchStraordinaryInspectionDate = new Date(
        watchProssimaIspezioneStraordinaria
      )
        .toISOString()
        .split('T')[0];

      if (nextStraordinaryInspectionDate === watchStraordinaryInspectionDate) {
        setValue('tipologiaStraordinariaLineeGuida', true);
        setValue('tipologiaStraordinariaIspettore', false);
      } else {
        setValue('tipologiaStraordinariaLineeGuida', false);
        setValue('tipologiaStraordinariaIspettore', true);
      }
    }

    if (!watchProssimaIspezioneStraordinaria) {
      setValue('tipologiaStraordinariaLineeGuida', false);
      setValue('tipologiaStraordinariaIspettore', false);
    }

    if (
      watchProssimaIspezioneStraordinaria &&
      !dataProssimaIspezioneStraordinariaCalculated
    ) {
      setValue('tipologiaStraordinariaLineeGuida', false);
      setValue('tipologiaStraordinariaIspettore', true);
    }
  }, [
    dataProssimaIspezioneStraordinariaCalculated,
    watchProssimaIspezioneStraordinaria,
  ]);

  useEffect(() => {
    if (!inspectionId) return;
    getInspectionDatesCalculated();
  }, [inspectionId]);

  // api
  const getInspectionDatesCalculated = async () => {
    try {
      const res = await get(
        `${INSPECTIONS_ROOT}/${inspectionId}/next-inspection-dates`
      );
      setNexInspectionDates(res);
    } catch (err) {
      console.error(err);
    }
  };
  // -------------------------------- PROSSIME ISPEZIONI --------------------------------

  return (
    <>
      <GridWrap container spacing={2} padding="20px">
        <GridWrap marginBottom={-25} marginTop={16} item xs={12}>
          <TitleSection>
            <Typography variant="h3BoldBlue">
              RISCHIO STRUTTURALE E SISMICO
            </Typography>
          </TitleSection>
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFTextField
            name="rilevatoreRischioStrutturale"
            label={'Rilevatore*'}
            inputProps={{ maxLength: 50 }}
            defaultValue=""
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFDatePicker
            label="Data ispezione*"
            name="dataIspezioneRischioStrutturale"
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFSelect
            name="accessoSottoPonte"
            label={'Possibile accedere al di sotto del ponte*'}
            defaultValue={''}
            selectOptions={PossibileAccessoSottoPonteOptions}
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
        <InfoWrapper
          size={3}
          infoMessage={INFO.accessoSottoPonteParziale.infoMessage}
        >
          <RHFNumberTextField
            name="accessoSottoPonteParziale"
            label={'Parziale'}
            defaultValue=""
            decimalScale={0}
            withValueLimit={({ value }) => value < 10000}
            disabled={
              !watchPossibileAccessoSottoPonteParziale ||
              inspectionDisabled ||
              isViewingInspection
            }
          />
        </InfoWrapper>
        <InfoWrapper
          size={3}
          infoDrawer={true}
          drawerTitle={INFO.prospettiIspezionati.drawerTitle}
          drawerImages={INFO.prospettiIspezionati.drawerImages}
          drawerText={INFO.prospettiIspezionati.drawerText}
        >
          <RHFMultiSelect
            name="prospettiIspezionati"
            label={'Prospetti ispezionati*'}
            defaultValue={[]}
            selectOptions={ProspettiIspezionatiOptions}
            disabled={inspectionDisabled || isViewingInspection}
          />
        </InfoWrapper>
      </GridWrap>

      <GridWrap container spacing={2} padding="20px">
        <GridWrap marginBottom={-25} marginTop={35} item xs={12}>
          <TitleSection>
            <Typography variant="h3BoldBlue">RISCHIO FRANE</Typography>
          </TitleSection>
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFSelect
            name="rischioFrana"
            label={'Rischio frana'}
            defaultValue={''}
            selectOptions={RischioFranaOptions}
            disabled
          />
        </GridWrap>
        <InfoWrapper
          size={3}
          //infoMessage={INFO.affidabilitaComplessivaRischioFrane.infoMessage}
          infoDrawer={true}
          drawerTitle={INFO.affidabilitaComplessivaRischioFrane.drawerTitle}
          drawerText={INFO.affidabilitaComplessivaRischioFrane.drawerText}
        >
          <RHFSelect
            name="affidabilitaComplessivaRischioFrane"
            label={'Affidabilità complessiva*'}
            defaultValue={''}
            selectOptions={AffidabilitaComplessivaRischioFraneOptions}
            disabled={inspectionDisabled || isViewingInspection}
          />
        </InfoWrapper>
        <GridWrap item xs={3}>
          <RHFTextField
            name="rilevatoreRischioFrane"
            label={'Rilevatore rischio frane*'}
            inputProps={{ maxLength: 50 }}
            defaultValue=""
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFTextField
            name="qualificaRilevatoreFrane"
            label={'Qualifica rilevatore frane'}
            inputProps={{ maxLength: 100 }}
            defaultValue=""
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFDatePicker
            label="Data ispezione rischio frane*"
            name="dataIspezioneRischioFrane"
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
      </GridWrap>

      <GridWrap container spacing={2} padding="20px">
        <GridWrap marginBottom={-25} marginTop={35} item xs={12}>
          <TitleSection>
            <Typography variant="h3BoldBlue">RISCHIO IDRAULICO</Typography>
          </TitleSection>
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFSelect
            name="rischioIdraulico"
            label={'Rischio idraulico'}
            defaultValue={''}
            selectOptions={RischioIdraulicoOptions}
            disabled
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFSelect
            name="affidabilitaComplessivaValutazioneRischioIdraulico"
            label={'Affidabilità complessiva*'}
            defaultValue={''}
            selectOptions={AffidabilitaComplessivaRischioIdraulicoOptions}
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFTextField
            name="rilevatoreRischioIdraulico"
            label={'Rilevatore rischio idraulico*'}
            inputProps={{ maxLength: 50 }}
            defaultValue=""
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFDatePicker
            label="Data ispezione rischio idraulico*"
            name="dataIspezioneRischioIdraulico"
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
        <GridWrap item xs={3}>
          <RHFSelect
            name="possibilitaVerificaScalzamentoFondazioni"
            label={'Possibilità verifica scalzamento fondazioni*'}
            defaultValue={''}
            selectOptions={VerificaScalzamentoFondazioniOptions}
            disabled={inspectionDisabled || isViewingInspection}
          />
        </GridWrap>
      </GridWrap>

      <GridWrap container spacing={2} padding="20px">
        <GridWrap marginBottom={-25} marginTop={35} item xs={12}>
          <TitleSection>
            <Typography variant="h3BoldBlue">OSSERVAZIONI</Typography>
          </TitleSection>
        </GridWrap>
        <InfoWrapper
          size={12}
          infoMessage={INFO.descrizioneAttivitaIspezione.infoMessage}
        >
          <RHFTextField
            name="descrizioneAttivitaIspezione"
            label={'Descrizione delle attività'}
            inputProps={{ maxLength: 2000 }}
            defaultValue=""
            disabled={inspectionDisabled || isViewingInspection}
          />
        </InfoWrapper>
      </GridWrap>

      <GridWrap container spacing={2} padding="20px">
        <GridWrap marginBottom={-25} marginTop={35} item xs={12}>
          <TitleSection>
            <Typography variant="h3BoldBlue">PROSSIME ISPEZIONI</Typography>
          </TitleSection>
        </GridWrap>
        <InfoWrapper
          size={3}
          infoMessage={INFO.dataProssimaIspezioneOrdinaria.infoMessage}
          legislationMessage={
            INFO.dataProssimaIspezioneOrdinaria.legislationMessage
          }
        >
          <RHFDatePicker
            label={'Data prossima ispezione ordinaria'}
            name="dataProssimaIspezioneOrdinaria"
            disabled={inspectionDisabled || isViewingInspection}
            defaultValue={null}
          />
        </InfoWrapper>
        <GridWrap item xs={4}>
          <CheckboxContainer>
            <RHFCheckbox
              name="tipologiaOrdinariaLineeGuida"
              label="Consigliata da LL.GG. (opere di tipo 2)"
              disabled
              defaultValue={false}
            />
            <RHFCheckbox
              name="tipologiaOrdinariaIspettore"
              label="Proposta da Ispettore"
              disabled
              defaultValue={false}
            />
          </CheckboxContainer>
        </GridWrap>
      </GridWrap>

      <GridWrap container spacing={2} padding="20px" marginTop={-20}>
        <InfoWrapper
          size={3}
          infoMessage={INFO.dataProssimaIspezioneOrdinaria.infoMessage}
          legislationMessage={
            INFO.dataProssimaIspezioneOrdinaria.legislationMessage
          }
        >
          <RHFDatePicker
            label={'Data prossima ispezione straordinaria'}
            name="dataProssimaIspezioneStraordinaria"
            disabled={inspectionDisabled || isViewingInspection}
            defaultValue={null}
          />
        </InfoWrapper>
        <GridWrap item xs={4}>
          <CheckboxContainer>
            <RHFCheckbox
              name="tipologiaStraordinariaLineeGuida"
              label="Consigliata da LL.GG. (opere di tipo 2)"
              disabled
              defaultValue={false}
            />
            <RHFCheckbox
              name="tipologiaStraordinariaIspettore"
              label="Proposta da Ispettore"
              disabled
              defaultValue={false}
            />
          </CheckboxContainer>
        </GridWrap>
      </GridWrap>
    </>
  );
};

export { Accordion2InspectionInformationL1 };
